import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { FaTrashAlt } from 'react-icons/fa';
import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../services/api';

import { useToast } from '../../hooks/toast';

import Input from '../../components/Input';
import SelectSync from '../../components/SelectSync';
import Loading from '../../components/Loading';
import Modal from '../../components/Modal';
import Paginacao from '../../components/Paginacao';
import SelectAsync from '../../components/SelectAsync';

import getValidationErrors from '../../utils/getValidationErrors';

import { OrcamentoLinha, OrcamentoLista } from './styles';

import getResponseErrors from '../../utils/getResponseErrors';
import { IOrcamento } from '../PacienteAtendimento/Odonto';
import { formatPrice, retornaStatusTratamento } from '../../utils/funcoes';
import PacienteCadastro from '../../components/PacienteCadastro';
import { IPacienteMaisCartaoDTO } from '../../components/Agendamento';

interface ProfileFormDados {
  pacienteID: string;
}
export interface ITratamento extends IOrcamento {
  paciente: {
    PacCodigo: number;
    PacNome: string;
  };
  created_at: Date;
  dataFormatada: string;
  valorFormatado: string;
  statusFormatado: string;
  PrgCodigo?: number;
  PrgDescricao: string;
}
interface IFormBusca {
  PacNome: string;
  tipo: number;
}

interface IPaginacao {
  PacNome?: string;
  tipo?: number;
  limite: number;
  pagina: number;
}

interface IAgendamentoSelecionado {
  AgeCodigo: number;
  AgeConfCodigo: number;
}

export interface IOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

const ListaOrcamentos: React.FC = () => {
  const { addToast } = useToast();
  const history = useHistory();
  const formRefBusca = useRef<FormHandles>(null);
  const formRefPaciente = useRef<FormHandles>(null);
  const [tratamentoSelecionado, setTratamentoSelecionado] = useState<
    number | undefined
  >();
  const [tratamentos, setTratamentos] = useState<ITratamento[] | undefined>(
    undefined,
  );
  const [showModalPacienteCad, setShowModalPacienteCad] = useState(false);
  const optionsPerfis: IOption[] = [
    { label: 'ODONTOLOGIA', value: '1' },
    { label: 'PACOTE', value: '2' },
  ];

  const [loading, setLoading] = useState(false);
  const [showSelectPaciente, setShowSelectPaciente] = useState(false);

  const [pacienteID, setPacienteID] = useState<number | undefined>();

  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );

  function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();

  const initialData = useMemo(() => {
    const PedPerCodigo = query.get('perfil') || 0;
    const PedFinalizado = query.get('status') || 'false';

    return { PedPerCodigo, PedFinalizado };
  }, [query]);

  const handleSubmitBusca = useCallback(async (dados: IFormBusca) => {
    try {
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        PacNome: Yup.string(),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      setTratamentos(undefined);
      const { PacNome, tipo } = dados;
      setPaginacao({
        ...(PacNome.toString().length > 1 ? { PacNome } : {}),
        ...(tipo.toString().length > 0 ? { tipo: Number(tipo) } : {}),
        pagina: 1,
        limite: 20,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      }
    }
  }, []);

  useEffect(() => {
    formRefBusca.current?.submitForm();
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;

        let parametros = `pagina=${paginacao.pagina}&limite=${paginacao.limite}`;
        const { PacNome, tipo } = paginacao;
        if (PacNome) parametros += `&PacNome=${PacNome}`;
        if (tipo) parametros += `&tipo=${tipo}`;

        const response = await api.get(`/tratamento?${parametros}`);
        const responseApi: ITratamento[] = response.data[0];

        setTotalRegistros(
          response.data[1] === 0 ? undefined : response.data[1],
        );

        const tratamentosFormatados = responseApi.map(trat => {
          return {
            ...trat,
            dataFormatada: format(
              parseISO(trat.created_at.toString()),
              'dd/MM/yyyy',
            ),
            valorFormatado: formatPrice(trat.valor),
            statusFormatado: retornaStatusTratamento(
              trat.validade,
              trat.finalizado,
              trat.tipo,
              trat.orcamento,
            ),
          };
        });
        setTratamentos(tratamentosFormatados);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar Pedido de Exames',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao]);

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  const handleFecharOrcamento = useCallback(
    (id: number, plano: boolean, tipo: number) => {
      if (tipo === 1) {
        history.push(`orcamento/${id}`, { plano });
        return;
      }
      history.push(`pacotetratamento/edit/${id}`, { plano });
    },
    [history],
  );

  const handleSelectClienteChange = useCallback(({ value }) => {
    if (value) {
      setPacienteID(Number(value));
    }
  }, []);

  const handleSubmitPaciente = useCallback((dados: ProfileFormDados) => {
    console.log('foi', dados);
  }, []);

  const handleCadastrouPaciente = useCallback((PacCodigo: number) => {
    setShowModalPacienteCad(false);
    setPacienteID(PacCodigo);
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      try {
        setLoading(true);
        const response = await api.get<IPacienteMaisCartaoDTO>(
          `/pacientes/pacientemaiscartao/${pacienteID}`,
        );

        history.push(`pacotetratamento/add/`, { paciente: response.data });
      } catch (error) {
        addToast({
          title: 'Não foi possível buscar o paciente',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    }
    if (pacienteID) loadDados();
  }, [pacienteID, addToast, history]);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}

      {showSelectPaciente && (
        <Modal
          show={showSelectPaciente}
          titulo="Adicionar Tratamento"
          handleFecharModal={
            () => {
              setShowSelectPaciente(false);
            }
            // eslint-disable-next-line react/jsx-curly-newline
          }
        >
          <>
            {loading && <Loading backgroundBlack={false} />}
            <Form
              ref={formRefPaciente}
              style={{ minHeight: '300px', padding: '.5rem' }}
              onSubmit={handleSubmitPaciente}
            >
              <SelectAsync
                name="pacienteID"
                label="Nome do Paciente"
                urlApi="pacientes/combo?termoBusca="
                placeholder="Digite para buscar"
                handleSelectChange={handleSelectClienteChange}
              />

              <Button
                className={loading ? 'd-none' : 'mt-3'}
                variant="outline-primary"
                size="sm"
                onClick={() => setShowModalPacienteCad(true)}
                disabled={loading}
              >
                Novo Paciente
              </Button>
            </Form>
          </>
        </Modal>
      )}

      {showModalPacienteCad && (
        <Modal
          titulo="Cadastro de Paciente"
          show={showModalPacienteCad}
          handleFecharModal={
            () => {
              setShowModalPacienteCad(false);
            }
            // eslint-disable-next-line react/jsx-curly-newline
          }
        >
          <PacienteCadastro
            exibeMenu={false}
            pacCodigo={undefined}
            handleRetornaDados={
              dados => handleCadastrouPaciente(dados.PacCodigo)
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </Modal>
      )}
      <div className="mb-2 p-2 bg-white border rounded">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 id="topoBusca">Pesquisa Orçamentos/Tratamentos</h6>
          <div>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={() => history.push('/agendas')}
            >
              Voltar para agendas
            </Button>
            <Button
              type="button"
              size="sm"
              className="ml-3"
              variant="primary"
              onClick={
                () => setShowSelectPaciente(true) // handleAddPacoteTratamento(true)
              }
            >
              ADICIONAR
            </Button>
          </div>
        </div>
        {optionsPerfis && optionsPerfis.length > 0 && (
          <Form
            ref={formRefBusca}
            initialData={initialData}
            onSubmit={handleSubmitBusca}
          >
            <Row noGutters>
              <Col sm={12} md={5} lg={3} className="pr-1">
                <Input
                  name="PacNome"
                  label="Nome"
                  maxLength={100}
                  minLength={3}
                />
              </Col>
              {/* <Col sm={12} md={4} lg={2} className="pr-1">
                 <SelectSync
                name="PedPerCodigo"
                label="Perfil"
                isClearable
                handleSelectChange={handleOnChangeVazio}
                options={optionsPerfil.optionPerfis}
              />
              </Col> */}
              <Col sm={6} md={4} lg={2} className="pr-1">
                <SelectSync
                  name="tipo"
                  label="tipo"
                  handleSelectChange={handleOnChangeVazio}
                  options={optionsPerfis}
                  isClearable
                />
              </Col>
              <Col sm={12} md={3} lg={1} className="pr-1">
                <Button
                  type="submit"
                  size="sm"
                  className="mt-3"
                  variant="primary"
                >
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </div>

      <div className="bg-white border p-2 rounded">
        {tratamentos ? (
          <>
            <Row noGutters className="bg-light py-2 border-top border-bottom">
              <Col md={1} lg={1}>
                <b>Data</b>
              </Col>
              <Col md={4} lg={4}>
                <b>Paciente</b>
              </Col>

              <Col md={2} lg={2}>
                <b>Descrição</b>
              </Col>
              <Col md={1} lg={1}>
                <b>Valor</b>
              </Col>
              <Col md={1} lg={1}>
                <b>Finalizado</b>
              </Col>
              <Col md={2} lg={2} />
              {/* <Col md={1} lg={1} /> */}
            </Row>
            {tratamentos.map(orcamento => (
              <OrcamentoLinha
                className="row no-gutters"
                key={`orcamento-${orcamento.id}`}
                data-id={orcamento.id}
              >
                <div className="col-md-1 col-lg-1">
                  <OrcamentoLista>{orcamento.dataFormatada}</OrcamentoLista>
                </div>
                <div className="col-md-4 col-lg-4">
                  <OrcamentoLista>{orcamento.paciente.PacNome}</OrcamentoLista>
                </div>
                <div className="col-md-2 col-lg-2">
                  <OrcamentoLista>{orcamento.PrgDescricao}</OrcamentoLista>
                </div>

                <div className="col-md-1 col-lg-1">
                  <OrcamentoLista>{orcamento.valorFormatado}</OrcamentoLista>
                </div>
                <div className="col-md-1 col-lg-1">
                  <OrcamentoLista>{orcamento.statusFormatado}</OrcamentoLista>
                </div>

                <div className={`col-md-2 col-lg-2 `}>
                  <OrcamentoLista>
                    <Button
                      type="button"
                      variant={orcamento.orcamento ? 'primary' : 'outline-info'}
                      size="sm"
                      title={`orcamento-${orcamento.id}`}
                      onClick={
                        () =>
                          handleFecharOrcamento(
                            orcamento.id,
                            orcamento.plano,
                            orcamento.tipo,
                          )
                        // eslint-disable-next-line react/jsx-curly-newline
                      }
                    >
                      {orcamento.orcamento ? 'Fechar Contrato' : 'Ver Contrato'}
                    </Button>
                    {/* <Button
                      type="button"
                      variant="outline-success"
                      size="sm"
                      data-id={orcamento.id}
                      onClick={() => {
                        setTratamentoSelecionado(orcamento.id);
                        setShowSelectPaciente(true);
                      }}
                    >
                      Fechar Contrato
                    </Button> */}
                  </OrcamentoLista>
                </div>
                {/* <div className="col-md-1 col-lg-1">
                  {orcamento.orcamento && (
                    <OrcamentoLista>
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        data-id={orcamento.id}
                        onClick={() => handleDelete(orcamento.id)}
                      >
                        <FaTrashAlt size={18} />
                      </Button>
                    </OrcamentoLista>
                  )}
                </div> */}
              </OrcamentoLinha>
            ))}
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>
              Pesquise pelo nome do paciente, nome do médico, status ou tipo.
            </h5>
          </div>
        )}

        <Paginacao
          limitePagina={paginacao?.limite || 20}
          paginasVizinhas={2}
          totalRegistros={totalRegistros}
          paginaInicio={paginacao?.pagina}
          handleProximaPagina={handleProximaPagina}
        />
      </div>
    </Container>
  );
};

export default ListaOrcamentos;
